import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3, Plus, Trash2, X, Check,
  AlertTriangle, ArrowUpRight, ArrowDownRight, Eye, Clock, Receipt, PiggyBank,
  Target, Zap, Building2, FileBarChart
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, PieChart, Pie, Legend, Area, AreaChart, ComposedChart, ReferenceLine,
  ScatterChart, Scatter, ZAxis
} from 'recharts';
import toast from 'react-hot-toast';
import './profitability.css';

const BUDGET_CATS = {
  materials: { label: 'Materiales', color: '#D4A017' },
  labor: { label: 'Mano de obra', color: '#3B82F6' },
  equipment_rental: { label: 'Alq. equipos', color: '#8B5CF6' },
  transport: { label: 'Transporte', color: '#06B6D4' },
  permits: { label: 'Permisos', color: '#F59E0B' },
  engineering: { label: 'Ingeniería', color: '#10B981' },
  subcontract: { label: 'Subcontratas', color: '#EC4899' },
  overhead: { label: 'Gastos gen.', color: '#64748B' },
  contingency: { label: 'Contingencia', color: '#EF4444' },
  other: { label: 'Otros', color: '#78716C' },
};

const COLLECTION_STATUS = {
  pending: { label: 'Pendiente', color: '#F59E0B' },
  partial: { label: 'Parcial', color: '#3B82F6' },
  collected: { label: 'Cobrado', color: '#10B981' },
  overdue: { label: 'Vencido', color: '#EF4444' },
  disputed: { label: 'Disputa', color: '#8B5CF6' },
};

const REVENUE_TYPES = [
  { value: 'invoice', label: 'Factura' },
  { value: 'milestone_payment', label: 'Pago hito' },
  { value: 'advance', label: 'Anticipo' },
  { value: 'retention_release', label: 'Lib. retención' },
  { value: 'variation_order', label: 'Variación obra' },
  { value: 'credit_note', label: 'Nota crédito' },
  { value: 'other', label: 'Otro' },
];

const fmt = (v) => Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtDec = (v) => Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;
const fmtK = (v) => `${(Number(v || 0) / 1000).toFixed(0)}k`;

export default function Profitability() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectDetail, setProjectDetail] = useState(null);
  const [compare, setCompare] = useState([]);
  const [collections, setCollections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateRevenue, setShowCreateRevenue] = useState(false);
  const [revenueForm, setRevenueForm] = useState({
    concept: '', type: 'invoice', reference: '', milestoneId: '',
    amount: '', taxAmount: '', invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '', description: ''
  });

  useEffect(() => {
    Promise.all([
      api.get('/profitability/dashboard'),
      api.get('/projects'),
    ]).then(([d, p]) => {
      setDashboard(d.data);
      setProjects(p.data.data || p.data || []);
    }).catch(() => { }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'compare') {
      api.get('/profitability/compare').then(r => setCompare(r.data)).catch(() => { });
    }
    if (tab === 'collections') {
      api.get('/profitability/collections').then(r => setCollections(r.data)).catch(() => { });
    }
  }, [tab]);

  useEffect(() => {
    if (selectedProject) {
      api.get(`/profitability/project/${selectedProject}`).then(r => setProjectDetail(r.data)).catch(() => { });
    }
  }, [selectedProject]);

  const loadDashboard = () => {
    api.get('/profitability/dashboard').then(r => setDashboard(r.data)).catch(() => { });
  };

  const createRevenue = async () => {
    if (!revenueForm.concept || !revenueForm.amount) return toast.error('Concepto e importe requeridos');
    try {
      await api.post('/profitability/revenues', {
        projectId: selectedProject, ...revenueForm,
        amount: parseFloat(revenueForm.amount), taxAmount: parseFloat(revenueForm.taxAmount) || 0
      });
      toast.success('Ingreso registrado');
      setShowCreateRevenue(false);
      setRevenueForm({
        concept: '', type: 'invoice', reference: '', milestoneId: '',
        amount: '', taxAmount: '', invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '', description: ''
      });
      api.get(`/profitability/project/${selectedProject}`).then(r => setProjectDetail(r.data)).catch(() => { });
      loadDashboard();
    } catch (e) { toast.error('Error al registrar ingreso'); }
  };

  const markCollected = async (id) => {
    try {
      const rev = collections?.pending?.find(r => r.id === id) || projectDetail?.revenues?.find(r => r.id === id);
      await api.put(`/profitability/revenues/${id}`, {
        collectionStatus: 'collected',
        collectedAmount: rev?.total_amount || 0,
        collectedDate: new Date().toISOString().split('T')[0]
      });
      toast.success('Marcado como cobrado');
      if (tab === 'collections') api.get('/profitability/collections').then(r => setCollections(r.data)).catch(() => { });
      if (selectedProject) api.get(`/profitability/project/${selectedProject}`).then(r => setProjectDetail(r.data)).catch(() => { });
      loadDashboard();
    } catch (e) { toast.error('Error'); }
  };

  const deleteRevenue = async (id) => {
    if (!window.confirm('¿Eliminar este ingreso?')) return;
    try {
      await api.delete(`/profitability/revenues/${id}`);
      toast.success('Eliminado');
      if (selectedProject) api.get(`/profitability/project/${selectedProject}`).then(r => setProjectDetail(r.data));
      loadDashboard();
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const d = dashboard;
  const totalRev = parseFloat(d?.revenues?.total_invoiced) || 0;
  const totalCol = parseFloat(d?.revenues?.total_collected) || 0;
  const totalExp = parseFloat(d?.costs?.total_expenses) || 0;
  const globalProfit = totalRev - totalExp;
  const globalMargin = totalRev > 0 ? ((globalProfit / totalRev) * 100) : 0;

  return (
    <div className="profitability-page">
      <div className="page-header">
        <div>
          <h1><TrendingUp size={24} /> Rentabilidad</h1>
          <p className="subtitle">Panel de análisis financiero</p>
        </div>
      </div>

      {/* Tabs - uses existing tab-btn system */}
      <div className="filters-bar">
        <div className="tab-buttons">
          <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>Dashboard</button>
          <button className={`tab-btn ${tab === 'project' ? 'active' : ''}`} onClick={() => setTab('project')}>Por Proyecto</button>
          <button className={`tab-btn ${tab === 'compare' ? 'active' : ''}`} onClick={() => setTab('compare')}>Comparativa</button>
          <button className={`tab-btn ${tab === 'collections' ? 'active' : ''}`} onClick={() => setTab('collections')}>Cobros</button>
        </div>
        {tab === 'project' && (
          <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
            <option value="">Seleccionar proyecto...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
          </select>
        )}
        {tab === 'project' && selectedProject && (
          <button className="btn btn-primary" onClick={() => setShowCreateRevenue(true)}>
            <Plus size={16} /> Nuevo Ingreso
          </button>
        )}
      </div>

      {/* ============ DASHBOARD GLOBAL ============ */}
      {tab === 'dashboard' && d && (
        <>
          <div className="prof-kpis">
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#10B98120', color: '#10B981' }}><ArrowUpRight size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmt(totalRev)}</span>
                <span className="prof-kpi-lbl">Ingresos Facturados</span>
              </div>
            </div>
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#EF444420', color: '#EF4444' }}><ArrowDownRight size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmt(totalExp)}</span>
                <span className="prof-kpi-lbl">Costes Totales</span>
              </div>
            </div>
            <div className={`prof-kpi ${globalProfit >= 0 ? 'positive' : 'negative'}`}>
              <div className="prof-kpi-icon" style={{ background: globalProfit >= 0 ? '#10B98120' : '#EF444420', color: globalProfit >= 0 ? '#10B981' : '#EF4444' }}>
                <PiggyBank size={20} />
              </div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmt(globalProfit)}</span>
                <span className="prof-kpi-lbl">Beneficio Bruto</span>
              </div>
            </div>
            <div className={`prof-kpi ${globalMargin >= 15 ? 'positive' : globalMargin >= 0 ? '' : 'negative'}`}>
              <div className="prof-kpi-icon" style={{ background: '#D4A01720', color: '#D4A017' }}><Target size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmtPct(globalMargin)}</span>
                <span className="prof-kpi-lbl">Margen Global</span>
              </div>
            </div>
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#F59E0B20', color: '#F59E0B' }}><Clock size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmt(d.revenues?.pending_collection)}</span>
                <span className="prof-kpi-lbl">Pte. Cobro</span>
              </div>
            </div>
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#3B82F620', color: '#3B82F6' }}><Zap size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{d.kpis?.active_projects || 0}</span>
                <span className="prof-kpi-lbl">Proyectos Activos</span>
              </div>
            </div>
          </div>

          {d.monthly?.length > 0 && (
            <div className="bud-chart-card">
              <h3>Evolución Mensual: Ingresos vs Costes</h3>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={d.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={fmtK} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
                    formatter={(v, name) => [fmt(v), name === 'revenue' ? 'Ingresos' : name === 'cost' ? 'Costes' : 'Beneficio']} />
                  <Legend formatter={(v) => v === 'revenue' ? 'Ingresos' : v === 'cost' ? 'Costes' : 'Beneficio'} />
                  <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} name="revenue" />
                  <Bar dataKey="cost" fill="#EF4444" radius={[4, 4, 0, 0]} name="cost" />
                  <Line type="monotone" dataKey="profit" stroke="#D4A017" strokeWidth={2} dot={false} name="profit" />
                  <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="prof-grid-2">
            {d.costByCategory?.length > 0 && (
              <div className="bud-chart-card">
                <h3>Costes por Categoría</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={d.costByCategory.map(c => ({
                      name: BUDGET_CATS[c.category]?.label || c.category,
                      value: parseFloat(c.actual) || 0,
                      color: BUDGET_CATS[c.category]?.color || '#64748B'
                    })).filter(c => c.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                      dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#475569' }}>
                      {d.costByCategory.map((c, i) => (
                        <Cell key={i} fill={BUDGET_CATS[c.category]?.color || '#64748B'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="bud-chart-card">
              <h3><AlertTriangle size={16} style={{ color: '#F59E0B' }} /> Alertas de Rentabilidad</h3>
              <div className="prof-alerts-list">
                {d.alerts?.length > 0 ? d.alerts.slice(0, 8).map((a, i) => (
                  <div key={i} className={`prof-alert prof-alert-${a.severity}`}>
                    <span className="prof-alert-badge">{a.project}</span>
                    <span className="prof-alert-msg">{a.message}</span>
                  </div>
                )) : <p className="prof-empty">Sin alertas activas</p>}
              </div>
            </div>
          </div>

          <div className="bud-chart-card">
            <h3>Rentabilidad por Proyecto</h3>
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Proyecto</th>
                    <th style={{ textAlign: 'right' }}>Contrato</th>
                    <th style={{ textAlign: 'right' }}>Ingresos</th>
                    <th style={{ textAlign: 'right' }}>Costes</th>
                    <th style={{ textAlign: 'right' }}>Beneficio</th>
                    <th style={{ textAlign: 'right' }}>Margen</th>
                    <th>Progreso</th>
                  </tr>
                </thead>
                <tbody>
                  {d.byProject?.map(p => {
                    const margin = parseFloat(p.margin_pct) || 0;
                    const target = parseFloat(p.target_margin_pct) || 15;
                    return (
                      <tr key={p.project_id} className="clickable-row" onClick={() => { setSelectedProject(p.project_id); setTab('project'); }}>
                        <td>
                          <span style={{ display: 'block', fontWeight: 600, fontSize: 13 }}>{p.code}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.name}</span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt(p.contract_value)}</td>
                        <td style={{ textAlign: 'right', color: '#10B981', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt(p.total_revenue)}</td>
                        <td style={{ textAlign: 'right', color: '#EF4444', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt(p.total_cost)}</td>
                        <td style={{ textAlign: 'right', color: parseFloat(p.gross_profit) >= 0 ? '#10B981' : '#EF4444', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          {fmt(p.gross_profit)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className={`prof-margin-badge ${margin >= target ? 'good' : margin >= 0 ? 'warn' : 'bad'}`}>
                            {fmtPct(margin)}
                          </span>
                        </td>
                        <td style={{ minWidth: 100 }}>
                          <div className="prof-progress">
                            <div className="prof-progress-track"><div style={{ width: `${p.progress_percentage || 0}%` }} /></div>
                            <span>{Math.round(p.progress_percentage || 0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!d.byProject?.length && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>Sin datos de proyecto</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ============ DETALLE POR PROYECTO ============ */}
      {tab === 'project' && projectDetail && (
        <>
          {(() => {
            const pr = projectDetail.project;
            const margin = parseFloat(pr.margin_pct) || 0;
            const target = parseFloat(pr.target_margin_pct) || 15;
            const budgetDev = pr.budget_estimated > 0
              ? (((pr.budget_actual - pr.budget_estimated) / pr.budget_estimated) * 100) : 0;
            return (
              <div className="prof-kpis">
                <div className="prof-kpi">
                  <div className="prof-kpi-icon" style={{ background: '#D4A01720', color: '#D4A017' }}><DollarSign size={20} /></div>
                  <div className="prof-kpi-content">
                    <span className="prof-kpi-val">{fmt(pr.contract_value)}</span>
                    <span className="prof-kpi-lbl">Valor Contrato</span>
                  </div>
                </div>
                <div className="prof-kpi">
                  <div className="prof-kpi-icon" style={{ background: '#10B98120', color: '#10B981' }}><ArrowUpRight size={20} /></div>
                  <div className="prof-kpi-content">
                    <span className="prof-kpi-val">{fmt(pr.total_revenue)}</span>
                    <span className="prof-kpi-lbl">Facturado</span>
                  </div>
                </div>
                <div className="prof-kpi">
                  <div className="prof-kpi-icon" style={{ background: '#EF444420', color: '#EF4444' }}><ArrowDownRight size={20} /></div>
                  <div className="prof-kpi-content">
                    <span className="prof-kpi-val">{fmt(pr.total_cost)}</span>
                    <span className="prof-kpi-lbl">Coste Total</span>
                  </div>
                </div>
                <div className={`prof-kpi ${parseFloat(pr.gross_profit) >= 0 ? 'positive' : 'negative'}`}>
                  <div className="prof-kpi-icon" style={{ background: parseFloat(pr.gross_profit) >= 0 ? '#10B98120' : '#EF444420', color: parseFloat(pr.gross_profit) >= 0 ? '#10B981' : '#EF4444' }}>
                    <PiggyBank size={20} />
                  </div>
                  <div className="prof-kpi-content">
                    <span className="prof-kpi-val">{fmt(pr.gross_profit)}</span>
                    <span className="prof-kpi-lbl">Beneficio</span>
                  </div>
                </div>
                <div className={`prof-kpi ${margin >= target ? 'positive' : margin >= 0 ? '' : 'negative'}`}>
                  <div className="prof-kpi-icon" style={{ background: '#D4A01720', color: '#D4A017' }}><Target size={20} /></div>
                  <div className="prof-kpi-content">
                    <span className="prof-kpi-val">{fmtPct(margin)} <small style={{ fontSize: '0.65em', opacity: 0.6 }}>/ {fmtPct(target)}</small></span>
                    <span className="prof-kpi-lbl">Margen / Objetivo</span>
                  </div>
                </div>
                <div className={`prof-kpi ${budgetDev > 10 ? 'negative' : budgetDev > 0 ? '' : 'positive'}`}>
                  <div className="prof-kpi-icon" style={{ background: budgetDev > 10 ? '#EF444420' : '#3B82F620', color: budgetDev > 10 ? '#EF4444' : '#3B82F6' }}>
                    <BarChart3 size={20} />
                  </div>
                  <div className="prof-kpi-content">
                    <span className="prof-kpi-val">{budgetDev > 0 ? '+' : ''}{budgetDev.toFixed(1)}%</span>
                    <span className="prof-kpi-lbl">Desv. Presupuesto</span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="prof-grid-2">
            {projectDetail.monthly?.length > 0 && (
              <div className="bud-chart-card">
                <h3>Evolución Mensual</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={projectDetail.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="label" stroke="#64748B" fontSize={10} />
                    <YAxis stroke="#64748B" fontSize={10} tickFormatter={fmtK} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} formatter={v => fmt(v)} />
                    <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B98130" name="Ingresos" />
                    <Area type="monotone" dataKey="cost" stroke="#EF4444" fill="#EF444430" name="Costes" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {projectDetail.costBreakdown?.length > 0 && (
              <div className="bud-chart-card">
                <h3>Estimado vs Real por Categoría</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={projectDetail.costBreakdown.map(c => ({
                    name: BUDGET_CATS[c.category]?.label || c.category,
                    estimado: parseFloat(c.estimated), real: parseFloat(c.actual),
                    color: BUDGET_CATS[c.category]?.color || '#64748B'
                  }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis type="number" stroke="#64748B" fontSize={10} tickFormatter={fmtK} />
                    <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={10} width={85} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} formatter={v => fmt(v)} />
                    <Bar dataKey="estimado" fill="#334155" name="Estimado" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="real" name="Real" radius={[0, 4, 4, 0]}>
                      {projectDetail.costBreakdown.map((c, i) => (
                        <Cell key={i} fill={BUDGET_CATS[c.category]?.color || '#64748B'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="prof-grid-2">
            {projectDetail.laborCosts?.length > 0 && (
              <div className="bud-chart-card">
                <h3>Coste Mano de Obra</h3>
                <div className="prof-mini-table">
                  {projectDetail.laborCosts.map((l, i) => (
                    <div key={i} className="prof-mini-row">
                      <span className="prof-mini-name">{l.crew_name} <small>({l.specialty || 'General'})</small></span>
                      <span className="prof-mini-detail">{l.total_days || 0} días · {l.assignments} asign.</span>
                      <span className="prof-mini-val">{fmt(l.total_cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {projectDetail.materialCosts?.length > 0 && (
              <div className="bud-chart-card">
                <h3>Top Materiales</h3>
                <div className="prof-mini-table">
                  {projectDetail.materialCosts.slice(0, 8).map((m, i) => (
                    <div key={i} className="prof-mini-row">
                      <span className="prof-mini-name">{m.name}</span>
                      <span className="prof-mini-detail">{Number(m.total_qty).toLocaleString('es')} {m.unit}</span>
                      <span className="prof-mini-val">{fmt(m.total_cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bud-chart-card">
            <h3>Ingresos / Facturas Emitidas</h3>
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Ref.</th><th>Concepto</th><th>Tipo</th>
                    <th style={{ textAlign: 'right' }}>Importe</th><th>Estado</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {projectDetail.revenues?.map(r => {
                    const cs = COLLECTION_STATUS[r.collection_status] || COLLECTION_STATUS.pending;
                    return (
                      <tr key={r.id}>
                        <td>{new Date(r.invoice_date).toLocaleDateString('es')}</td>
                        <td className="mono">{r.reference || '-'}</td>
                        <td>{r.concept}</td>
                        <td><span className="cl-badge" style={{ background: '#3B82F620', color: '#3B82F6' }}>
                          {REVENUE_TYPES.find(t => t.value === r.type)?.label || r.type}
                        </span></td>
                        <td style={{ textAlign: 'right', color: '#10B981', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtDec(r.total_amount)}</td>
                        <td><span className="cl-badge" style={{ background: cs.color + '20', color: cs.color }}>{cs.label}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {r.collection_status !== 'collected' && (
                            <button className="btn btn-sm btn-success" title="Marcar cobrado" onClick={() => markCollected(r.id)}>
                              <Check size={12} />
                            </button>
                          )}
                          {' '}
                          <button className="btn btn-sm btn-secondary" onClick={() => deleteRevenue(r.id)}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!projectDetail.revenues?.length && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>Sin ingresos registrados</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'project' && !selectedProject && (
        <div className="prof-empty-state"><Building2 size={48} /><p>Selecciona un proyecto</p></div>
      )}

      {/* ============ COMPARATIVA ============ */}
      {tab === 'compare' && (
        <>
          {compare.length > 0 && (
            <div className="bud-chart-card">
              <h3>Mapa de Rentabilidad (Tamaño = Valor contrato)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="plant_capacity_kwp" name="Capacidad kWp" stroke="#64748B" fontSize={11} label={{ value: 'kWp', position: 'bottom', fill: '#64748B' }} />
                  <YAxis dataKey="margin_pct" name="Margen %" stroke="#64748B" fontSize={11} label={{ value: 'Margen %', angle: -90, position: 'left', fill: '#64748B' }} />
                  <ZAxis dataKey="contract_value" range={[50, 400]} name="Contrato" />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
                    formatter={(v, name) => name === 'Contrato' ? fmt(v) : name === 'Margen %' ? fmtPct(v) : `${v} kWp`}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.code || ''} />
                  <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
                  <ReferenceLine y={15} stroke="#10B981" strokeDasharray="3 3" />
                  <Scatter data={compare.filter(c => c.plant_capacity_kwp > 0)} fill="#D4A017" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bud-chart-card">
            <h3>Comparativa de Proyectos</h3>
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Proyecto</th><th>kWp</th>
                    <th style={{ textAlign: 'right' }}>€/kWp Contrato</th>
                    <th style={{ textAlign: 'right' }}>€/kWp Coste</th>
                    <th style={{ textAlign: 'right' }}>Ingresos</th>
                    <th style={{ textAlign: 'right' }}>Costes</th>
                    <th style={{ textAlign: 'right' }}>Beneficio</th>
                    <th>Margen</th>
                  </tr>
                </thead>
                <tbody>
                  {compare.map(p => {
                    const margin = parseFloat(p.margin_pct) || 0;
                    return (
                      <tr key={p.project_id} className="clickable-row" onClick={() => { setSelectedProject(p.project_id); setTab('project'); }}>
                        <td>
                          <span style={{ display: 'block', fontWeight: 600, fontSize: 13 }}>{p.code}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.name}</span>
                        </td>
                        <td>{p.plant_capacity_kwp || '-'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.revenue_per_kwp ? `${Number(p.revenue_per_kwp).toLocaleString('es')} €` : '-'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.cost_per_kwp ? `${Number(p.cost_per_kwp).toLocaleString('es')} €` : '-'}</td>
                        <td style={{ textAlign: 'right', color: '#10B981', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt(p.total_revenue)}</td>
                        <td style={{ textAlign: 'right', color: '#EF4444', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt(p.total_cost)}</td>
                        <td style={{ textAlign: 'right', color: parseFloat(p.gross_profit) >= 0 ? '#10B981' : '#EF4444', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt(p.gross_profit)}</td>
                        <td><span className={`prof-margin-badge ${margin >= 15 ? 'good' : margin >= 0 ? 'warn' : 'bad'}`}>{fmtPct(margin)}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ============ COBROS PENDIENTES ============ */}
      {tab === 'collections' && collections && (
        <>
          <div className="prof-kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#F59E0B20', color: '#F59E0B' }}><Clock size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmt(collections.summary?.total_pending_amount)}</span>
                <span className="prof-kpi-lbl">{collections.summary?.total_pending || 0} facturas pendientes</span>
              </div>
            </div>
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#EF444420', color: '#EF4444' }}><AlertTriangle size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{fmt(collections.summary?.overdue_amount)}</span>
                <span className="prof-kpi-lbl">{collections.summary?.overdue_count || 0} facturas vencidas</span>
              </div>
            </div>
            <div className="prof-kpi">
              <div className="prof-kpi-icon" style={{ background: '#3B82F620', color: '#3B82F6' }}><Receipt size={20} /></div>
              <div className="prof-kpi-content">
                <span className="prof-kpi-val">{Math.round(collections.summary?.avg_days_outstanding || 0)} días</span>
                <span className="prof-kpi-lbl">Antigüedad media</span>
              </div>
            </div>
          </div>

          <div className="bud-chart-card">
            <h3>Cobros Pendientes</h3>
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Proyecto</th><th>Concepto</th><th>Ref.</th><th>Fecha</th><th>Vencimiento</th>
                    <th style={{ textAlign: 'right' }}>Importe</th><th style={{ textAlign: 'right' }}>Cobrado</th><th style={{ textAlign: 'right' }}>Pendiente</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {collections.pending?.map(r => {
                    const pending = (parseFloat(r.total_amount) || 0) - (parseFloat(r.collected_amount) || 0);
                    const isOverdue = r.is_overdue;
                    return (
                      <tr key={r.id} style={isOverdue ? { background: 'rgba(239,68,68,0.05)' } : {}}>
                        <td><span style={{ fontWeight: 600, fontSize: 13 }}>{r.project_code}</span></td>
                        <td>{r.concept}</td>
                        <td className="mono">{r.reference || '-'}</td>
                        <td>{new Date(r.invoice_date).toLocaleDateString('es')}</td>
                        <td>
                          {r.due_date ? new Date(r.due_date).toLocaleDateString('es') : '-'}
                          {isOverdue && <small style={{ display: 'block', color: '#EF4444', fontSize: 10 }}>{r.days_overdue}d vencido</small>}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtDec(r.total_amount)}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtDec(r.collected_amount)}</td>
                        <td style={{ textAlign: 'right', color: '#F59E0B', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtDec(pending)}</td>
                        <td>
                          <button className="btn btn-sm btn-success" title="Marcar cobrado" onClick={() => markCollected(r.id)}>
                            <Check size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!collections.pending?.length && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>Sin cobros pendientes</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal Nuevo Ingreso */}
      {showCreateRevenue && (
        <div className="modal-overlay" onClick={() => setShowCreateRevenue(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nuevo Ingreso</h3>
              <button className="btn-icon" onClick={() => setShowCreateRevenue(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo</label>
                  <select value={revenueForm.type} onChange={e => setRevenueForm(f => ({ ...f, type: e.target.value }))}>
                    {REVENUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Nº Referencia</label>
                  <input value={revenueForm.reference} onChange={e => setRevenueForm(f => ({ ...f, reference: e.target.value }))} placeholder="FA-2026-001" />
                </div>
              </div>
              <div className="form-group">
                <label>Concepto *</label>
                <input value={revenueForm.concept} onChange={e => setRevenueForm(f => ({ ...f, concept: e.target.value }))} placeholder="Certificación hito 1 - Cimentaciones" />
              </div>
              <div className="form-group">
                <label>Hito asociado</label>
                <select value={revenueForm.milestoneId} onChange={e => setRevenueForm(f => ({ ...f, milestoneId: e.target.value }))}>
                  <option value="">Sin hito</option>
                  {projectDetail?.milestones?.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({fmt(m.payment_amount)})</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Base imponible (€) *</label>
                  <input type="number" step="0.01" value={revenueForm.amount} onChange={e => setRevenueForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>IVA (€)</label>
                  <input type="number" step="0.01" value={revenueForm.taxAmount} onChange={e => setRevenueForm(f => ({ ...f, taxAmount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Total</label>
                  <input disabled value={((parseFloat(revenueForm.amount) || 0) + (parseFloat(revenueForm.taxAmount) || 0)).toFixed(2) + ' €'} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fecha factura</label>
                  <input type="date" value={revenueForm.invoiceDate} onChange={e => setRevenueForm(f => ({ ...f, invoiceDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Fecha vencimiento</label>
                  <input type="date" value={revenueForm.dueDate} onChange={e => setRevenueForm(f => ({ ...f, dueDate: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea rows={2} value={revenueForm.description} onChange={e => setRevenueForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateRevenue(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createRevenue}>Registrar Ingreso</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
